// Tiny hash-based client-side router. Hash routing avoids needing
// server-side rewrites for SPA paths — every URL keeps pointing at
// index.html.
//
// Routes are registered as `{ '#/path': pageFn }`. Each page function
// receives `(mountEl, params)` and may either render synchronously or
// return a Promise. Pages may also return a cleanup function that the
// router calls before navigating away — used by the simulator route to
// stop the rAF loop and detach keyboard listeners.

const routes = new Map();
let notFoundPage = null;
let mountEl = null;
let onChange = null;
let activeCleanup = null;

// Navigation guards: a page can register a function that runs before
// a route change resolves. Returning `false` aborts the navigation and
// reverts the URL — the simulator uses this to prompt before letting
// the user click out of an in-progress race. The guard runs once per
// attempt and clears itself if approved (so it won't double-prompt
// during the cleanup → resolve step). When a guard vetoes, we revert
// the hash and skip the whole resolve so the active page stays alive
// (no cleanup, no re-mount, race keeps running).
let activeGuard = null;
let lastResolvedHash = null;
let suppressResolve = false;
// Bumps every time resolve() starts. An async page function that's
// suspended (e.g. race.js awaiting sim.ready) can finish AFTER a
// later navigation has already swapped the page — in which case the
// cleanup it returns must be called immediately (to tear down rAFs,
// listeners, half-mounted overlays) instead of registered as the
// active page's cleanup. The generation check in resolve() catches
// that case.
let resolveGeneration = 0;

export function setNavigationGuard(fn) { activeGuard = fn; }
export function clearNavigationGuard() { activeGuard = null; }

export function registerRoute(hash, pageFn) {
  routes.set(hash, pageFn);
}

export function setNotFound(pageFn) {
  notFoundPage = pageFn;
}

export function start(mountElement, onRouteChange) {
  mountEl = mountElement;
  onChange = onRouteChange ?? null;
  window.addEventListener('hashchange', resolve);
  resolve();
}

export function navigate(hash) {
  if (window.location.hash === hash) {
    resolve();
  } else {
    window.location.hash = hash;
  }
}

async function resolve() {
  // We get here from a hashchange. If the previous resolve vetoed a
  // navigation and reverted the URL, skip the whole pipeline — we
  // don't want to tear down and re-mount the page that just refused
  // to leave.
  if (suppressResolve) {
    suppressResolve = false;
    return;
  }

  const myGen = ++resolveGeneration;
  const incomingHash = window.location.hash || '#/';

  // Navigation guard. Only fires when the hash actually changed (so
  // the initial load doesn't trigger it).
  if (
    typeof activeGuard === 'function'
    && lastResolvedHash != null
    && lastResolvedHash !== incomingHash
  ) {
    let allow;
    try { allow = activeGuard(); }
    catch (e) { console.error('Navigation guard threw:', e); allow = true; }
    if (!allow) {
      suppressResolve = true;
      window.location.hash = lastResolvedHash;
      return;
    }
    activeGuard = null;
  }

  // Tear down the previous page first so its rAF / listeners stop
  // before we replace innerHTML out from under it.
  if (typeof activeCleanup === 'function') {
    try { activeCleanup(); } catch (e) { console.error('Route cleanup failed:', e); }
    activeCleanup = null;
  }

  const raw = incomingHash;
  const [path, query] = raw.split('?');
  const params = parseParams(query);
  const page = routes.get(path) ?? notFoundPage;
  if (!page) {
    mountEl.innerHTML = `<section class="page"><h1>404</h1><p>No route matches <code>${escapeHtml(path)}</code>.</p></section>`;
    return;
  }
  mountEl.innerHTML = '';
  if (onChange) onChange(path);
  const result = await page(mountEl, params);
  // Superseded mid-render. A newer resolve has already torn down the
  // previous activeCleanup (which was null for us anyway, since we
  // hadn't registered yet) and rendered the next page. Whatever this
  // late-completing render mounted (e.g. race.js's pre-race modal
  // appearing on top of the play page) needs the cleanup called now
  // so its rAFs / listeners / overlays don't leak onto the active
  // page. Return without registering — we are NOT the active page.
  if (myGen !== resolveGeneration) {
    if (typeof result === 'function') {
      try { result(); } catch (e) { console.error('Superseded route cleanup failed:', e); }
    }
    return;
  }
  if (typeof result === 'function') activeCleanup = result;
  lastResolvedHash = incomingHash;
}

function parseParams(query) {
  const out = {};
  if (!query) return out;
  for (const part of query.split('&')) {
    if (!part) continue;
    const [k, v = ''] = part.split('=');
    out[decodeURIComponent(k)] = decodeURIComponent(v);
  }
  return out;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
